import { create } from 'zustand'
import { derived } from '@/lib/storeHelpers'

/**
 * Appearance preference: 'system' (default), 'light' or 'dark'.
 *
 * Reference port of a Pinia setup store: refs become state fields, computeds
 * become derived fields with the same names, and `watch` becomes a
 * subscription that runs the side effect.
 */

const THEME_KEY = 'nova-hockey-theme'

/** The class the design tokens key dark mode off (name kept from the Ionic era). */
const DARK_CLASS = 'ion-palette-dark'

export type ThemePreference = 'light' | 'dark' | 'system'
const VALID = new Set<ThemePreference>(['light', 'dark', 'system'])

const systemQuery = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

const getInitialPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_KEY) as ThemePreference | null
    if (stored && VALID.has(stored)) return stored
  } catch {
    // Storage blocked (private mode, embedded webview): fall back to system.
  }
  return 'system'
}

export interface ThemeState {
  preference: ThemePreference
  systemPrefersDark: boolean
  // derived
  resolvedTheme: 'light' | 'dark'
  isDark: boolean
  isLight: boolean
  followsSystem: boolean
  /** @deprecated read `resolvedTheme` (rendered) or `preference` (chosen) */
  theme: 'light' | 'dark'
  // actions
  toggleTheme: () => void
  setTheme: (next: ThemePreference) => void
  useSystemTheme: () => void
  applyTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
  derived<ThemeState>((s) => {
    const resolvedTheme = s.preference === 'system' ? (s.systemPrefersDark ? 'dark' : 'light') : s.preference
    return {
      resolvedTheme,
      theme: resolvedTheme,
      isDark: resolvedTheme === 'dark',
      isLight: resolvedTheme === 'light',
      followsSystem: s.preference === 'system'
    }
  })((set, get) => ({
    preference: getInitialPreference(),
    systemPrefersDark: systemQuery()?.matches ?? false,
    resolvedTheme: 'light',
    theme: 'light',
    isDark: false,
    isLight: true,
    followsSystem: true,

    toggleTheme: () => set({ preference: get().isDark ? 'light' : 'dark' }),
    setTheme: (next) => {
      if (VALID.has(next)) set({ preference: next })
    },
    useSystemTheme: () => set({ preference: 'system' }),

    applyTheme: () => {
      if (typeof document === 'undefined') return
      const { documentElement, body } = document
      const { preference, isDark } = get()

      // Partner embeds are always light.
      if (documentElement.classList.contains('embed-shell')) {
        documentElement.classList.remove(DARK_CLASS, 'dark-theme')
        documentElement.classList.add('light-theme')
        body?.classList.remove(DARK_CLASS, 'dark-theme')
        body?.classList.add('light-theme')
        documentElement.style.colorScheme = 'light'
      } else {
        documentElement.classList.toggle(DARK_CLASS, isDark)
        body?.classList.toggle(DARK_CLASS, isDark)
        documentElement.classList.toggle('dark-theme', isDark)
        documentElement.classList.toggle('light-theme', !isDark)
        body?.classList.toggle('dark-theme', isDark)
        body?.classList.toggle('light-theme', !isDark)
        // Native browser UI (form controls, scrollbars) follows this.
        documentElement.style.colorScheme = isDark ? 'dark' : 'light'
      }

      try {
        localStorage.setItem(THEME_KEY, preference)
      } catch {
        // Non-fatal: the theme still applies for this session.
      }
    }
  }))
)

// watch([preference, systemPrefersDark], applyTheme, { immediate: true })
useThemeStore.subscribe((state, prev) => {
  if (state.preference !== prev.preference || state.systemPrefersDark !== prev.systemPrefersDark) {
    state.applyTheme()
  }
})
useThemeStore.getState().applyTheme()

// Track OS changes so 'system' stays live without a reload.
systemQuery()?.addEventListener('change', (event) => {
  useThemeStore.setState({ systemPrefersDark: event.matches })
})
